import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, BadgeCheck, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

export default function RenewLicense() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(token ? "" : "Missing renewal token. Please use the link from your reminder email.");
  const [expires, setExpires] = useState("");
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) return;
    api.get(`/providers/renewal/${token}`)
      .then(({ data }) => { setInfo(data); setExpires(data.suggested_expires); setNumber(data.license_number || ""); })
      .catch((e) => setError(e?.response?.data?.detail || "This renewal link is invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/providers/renewal/${token}`, { license_expires: expires, license_number: number || null });
      setDone(data); toast.success("License marked as renewed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not renew"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="renew-page">
      <Card className="w-full max-w-md p-8 border-slate-200 shadow-none">
        <div className="mb-6"><Logo size="md" /></div>
        <Link to="/providers" className="text-xs text-slate-500 font-mono-plex inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> Open providers</Link>
        <div className="mb-6">
          <div className="text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-2">License renewal</div>
          <h2 className="font-heading text-3xl font-bold">Mark as renewed</h2>
          <p className="text-sm text-slate-500 mt-2">Confirm the new expiry date and the CRM updates instantly. No sign-in needed.</p>
        </div>
        {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="renew-error">{error}</div>}
        {done ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2" data-testid="renew-done">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> {done.name}'s license now expires on <b>{done.license_expires}</b>. Reminders reset.
          </div>
        ) : info && (
          <form onSubmit={submit} className="space-y-4">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="renew-provider">
              <div className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-slate-500" /><span className="font-medium">{info.name}</span><Badge variant="outline">{info.specialty}</Badge></div>
              <div className="text-xs text-slate-500 mt-1 font-mono-plex">Current expiry {info.license_expires || "—"} · {info.license_state || ""}</div>
            </div>
            <div>
              <Label htmlFor="exp">New expiry date</Label>
              <Input id="exp" type="date" required data-testid="renew-date" value={expires} onChange={(e) => setExpires(e.target.value)} className="mt-1.5" />
              <p className="text-[11px] text-slate-400 mt-1">Pre-filled with +2 years (Florida biennial cycle). Adjust if needed.</p>
            </div>
            <div>
              <Label htmlFor="num">License number (optional)</Label>
              <Input id="num" data-testid="renew-number" value={number} onChange={(e) => setNumber(e.target.value)} className="mt-1.5" />
            </div>
            <Button type="submit" disabled={busy || !expires} data-testid="renew-submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy ? "Saving…" : "Confirm renewal"}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
